import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion"; // ADD THIS IMPORT
import {
  Heart,
  Coffee,
  Code2,
  Mail,
  Phone,
  MapPin,
  ArrowUp,
  Sun,
  Moon,
  Sparkles,
  Copyright,
  Linkedin,
  Github,
  Twitter,
  Instagram,
  Send,
} from "lucide-react";

const Footer = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [theme, setTheme] = useState("dark");

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
    // You can implement actual theme switching logic here
  };

  const quickLinks = [
    { label: "Home", href: "#hero" },
    { label: "About", href: "#about" },
    { label: "Experience", href: "#experience" },
    { label: "Skills", href: "#skills" },
    { label: "Projects", href: "#projects" },
    { label: "Contact", href: "#contact" },
  ];

  const services = [
    "Frontend Development",
    "Digital Marketing",
    "SEO Optimization",
    "UI/UX Design",
    "Network Consulting",
    "Technical Training",
  ];

  const socialLinks = [
    {
      icon: <Linkedin />,
      label: "LinkedIn",
      url: "https://www.linkedin.com/in/prasad-savadatti-9262a8a5/",
      color: "hover:text-blue-400",
    },
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-900/10 to-dark"></div>

      {/* Floating Particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-primary-400/30 rounded-full"
            initial={{
              x: Math.random() * 100 + "%",
              y: Math.random() * 100 + "%",
              opacity: 0,
            }}
            animate={{
              y: [null, "-100vh"],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      {/* Main Footer Content */}
      <div className="relative z-10">
        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="container mx-auto px-4 max-w-6xl mb-12"
        >
          <div className="p-8 rounded-3xl glass-effect border border-white/10 text-center relative overflow-hidden">
            {/* Animated Border */}
            <div className="absolute inset-0 rounded-3xl p-[2px] bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 animate-gradient"></div>
            <div className="absolute inset-[2px] rounded-3xl bg-dark"></div>

            <div className="relative z-10">
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-effect mb-4">
                <Sparkles className="w-4 h-4 text-primary-400" />
                <span className="text-sm">Ready to Collaborate?</span>
              </div>

              <h2 className="text-3xl font-bold mb-4">
                Let's Build Something
                <span className="block gradient-text">Amazing Together</span>
              </h2>

              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Whether you have a project in mind or just want to chat about
                tech, I'm always open to new opportunities and collaborations.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <motion.a
                  href="mailto:prasad.savadatti@gmail.com"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full font-semibold flex items-center space-x-2 hover:shadow-lg hover:shadow-primary-500/30 transition-all"
                >
                  <Mail className="w-5 h-5" />
                  <span>Send Email</span>
                </motion.a>

                <motion.a
                  href="tel:+916361056819"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 border border-white/20 rounded-full font-semibold flex items-center space-x-2 hover:bg-white/10 transition-all"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call Now</span>
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer Content */}
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Brand Column */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500">
                  <Code2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Prasad Savadatti</h3>
                  <p className="text-sm text-gray-400">
                    Front End Developer & Digital Marketing
                  </p>
                </div>
              </div>
              <p className="text-gray-400 mb-6">
                Crafting exceptional digital experiences with modern
                technologies and creative solutions.
              </p>

              {/* Social Links */}
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ y: -3, scale: 1.1 }}
                    className={`p-2 rounded-lg glass-effect border border-white/10 text-gray-400 ${social.color} transition-colors`}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => (
                  <motion.li
                    key={index}
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-primary-400 transition-colors flex items-center space-x-2"
                    >
                      <div className="w-1 h-1 rounded-full bg-primary-500"></div>
                      <span>{link.label}</span>
                    </a>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                {services.map((service, index) => (
                  <motion.li
                    key={index}
                    whileHover={{ x: 5 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="text-gray-400 hover:text-primary-400 transition-colors flex items-center space-x-2">
                      <div className="w-1 h-1 rounded-full bg-secondary-500"></div>
                      <span>{service}</span>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-primary-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-400">Email</div>
                    <div>prasad.savadatti@gmail.com</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-primary-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-400">Phone</div>
                    <div>+91-6361056819</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-primary-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-400">Location</div>
                    <div>Bengaluru, Karnataka</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="py-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="flex items-center space-x-2 text-gray-400">
              <Copyright className="w-4 h-4" />
              <span>{currentYear} Prasad Savadatti. All rights reserved.</span>
            </div>

            {/* Made With Love */}
            <div className="flex items-center space-x-2 text-gray-400">
              <span>Made with</span>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <Heart className="w-4 h-4 text-red-500 fill-red-500" />
              </motion.div>
              <span>and</span>
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
              >
                <Coffee className="w-4 h-4 text-amber-500" />
              </motion.div>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg glass-effect border border-white/10 hover:bg-white/10 transition-colors"
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-blue-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 p-3 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 shadow-lg hover:shadow-primary-500/30 transition-all z-50"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowUp className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;
